import { Controller } from "@hotwired/stimulus"
import ScriptedVideoChannel from "../../channels/scripted_video_channel"

// Handles first-frame image interactions: generate via fetch + file upload submission.
// Subscribes to ActionCable for async job status updates.
export default class extends Controller {
  static targets = [
    "uploadForm", "fileInput", "sketchForm",
    "generateButton", "generateIcon", "generateSpinner", "generateLabel",
    "errorMessage",
    "lightbox", "lightboxImage"
  ]
  static values = { configId: String, sceneId: String }

  connect() {
    if (this.configIdValue) this.subscribeToChannel()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  async generate(event) {
    const button = event.currentTarget
    const url = button.dataset.url
    const sceneId = button.dataset.sceneId
    if (!url) return

    this.hideError()
    this.showGenerating()

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const body = new FormData()
      body.append("scene_id", sceneId)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || `Request failed (${response.status})`)
      }
      // Job enqueued — spinner stays visible, ActionCable will deliver the result
    } catch (error) {
      this.hideGenerating()
      this.showError(error.message || "Failed to start generation")
      console.error("[first-frame] Generate failed:", error.message)
    }
  }

  submitUpload() {
    if (!this.hasUploadFormTarget || !this.hasFileInputTarget) return
    if (this.fileInputTarget.files.length === 0) return

    this.uploadFormTarget.requestSubmit()
  }

  submitSketch(event) {
    if (!this.hasSketchFormTarget) return
    const input = event.currentTarget
    if (!input || input.files.length === 0) return

    this.sketchFormTarget.requestSubmit()
  }

  // --- Private ---

  subscribeToChannel() {
    this.subscription = ScriptedVideoChannel.subscribe(this.configIdValue, {
      onFirstFrameUpdate: (data) => this.handleFirstFrameUpdate(data)
    })
  }

  handleFirstFrameUpdate(data) {
    // Only handle updates for this scene
    if (data.scene_id !== this.sceneIdValue) return

    if (data.status === "completed") {
      this.turboReload()
    } else if (data.status === "failed") {
      this.hideGenerating()
      this.showError(data.error || "First-frame generation failed")
    }
  }

  showGenerating() {
    if (this.hasGenerateButtonTarget) {
      this.generateButtonTarget.disabled = true
      this.generateButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
    if (this.hasGenerateIconTarget) this.generateIconTarget.classList.add("hidden")
    if (this.hasGenerateSpinnerTarget) this.generateSpinnerTarget.classList.remove("hidden")
    if (this.hasGenerateLabelTarget) this.generateLabelTarget.textContent = "Generating..."
  }

  hideGenerating() {
    if (this.hasGenerateButtonTarget) {
      this.generateButtonTarget.disabled = false
      this.generateButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }
    if (this.hasGenerateIconTarget) this.generateIconTarget.classList.remove("hidden")
    if (this.hasGenerateSpinnerTarget) this.generateSpinnerTarget.classList.add("hidden")
    if (this.hasGenerateLabelTarget) this.generateLabelTarget.textContent = "Generate"
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove("hidden")
    }
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
      this.errorMessageTarget.textContent = ""
    }
  }

  openLightbox(event) {
    const url = event.currentTarget.dataset.imageUrl
    const name = event.currentTarget.dataset.imageName || ""
    if (!url || !this.hasLightboxTarget) return

    this.lightboxImageTarget.src = url
    this.lightboxImageTarget.alt = name
    this.lightboxTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  closeLightbox() {
    if (!this.hasLightboxTarget) return

    this.lightboxTarget.classList.add("hidden")
    this.lightboxImageTarget.src = ""
    document.body.classList.remove("overflow-hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  turboReload() {
    if (typeof Turbo !== "undefined" && Turbo.visit) {
      Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.location.reload()
    }
  }
}
